/**
 * This file was automatically generated by json-schema-to-typescript.
 * DO NOT MODIFY IT BY HAND. Instead, modify the source JSONSchema file,
 * and run json-schema-to-typescript to regenerate this file.
 */

/**
 * Connector configuration schema.
 */
export class Config {
  /**
   * Determines what type of network the connector is a part of. Can be: 'production', 'test'. Default: 'test'
   */
  env?: "production" | "test";
  /**
   * ILP address of the connector. This property can be omitted if an account with `relation=parent` is configured under `accounts`.
   */
  ilpAddress?: string;
  /**
   * If there are multiple parents, and `ilpAddress` is not set explicit, specify the account ID of the parent that we should load our address from. Defaults to the first parent in the `accounts` map.
   */
  ilpAddressInheritFrom?: string;
  /**
   * Description of connector account plugins.
   */
  accounts!: {
    /**
     * Description of individual account.
     */
    [k: string]: {
      /**
       * Relationship between the connector and the counterparty that the account is with.
       */
      relation: "parent" | "peer" | "child";
      /**
       * Name or instance of the ILP plugin that should be used for this account. A plugin instance can only be passed when instantiating the connector from JavaScript.
       */
      plugin:
        | string
        | {
            [k: string]: any;
          };
      /**
       * Currency code or other asset identifier that will be passed to the backend to select the correct rate for this account.
       */
      assetCode: string;
      /**
       * Interledger amounts are integers, but most currencies are typically represented as fractional units, e.g. cents. This property defines how many Interledger units make up one regular units. For dollars, this would usually be set to 9, so that Interledger amounts are expressed in nanodollars.
       */
      assetScale: number;
      /**
       * Defines whether the connector should maintain and enforce a balance for this account. The balance is always from the connector's perspective. Therefore, a negative balance implies the connector owes money to the counterparty and a positive balance implies the counterparty owes money to the connector. This setting is enforced by the built-in `balance` middleware.
       */
      balance?: {
        /**
         * Minimum balance (in this account's indivisible base units) the connector must maintain. The connector will reject outgoing packets if they would put it below this balance. The format is a string containing an integer (which may be prefixed with `-` to indicate a negative value), `"-Infinity"` or `"Infinity"`.
         */
        minimum?: string;
        /**
         * Maximum balance (in this account's indivisible base units) the connector will allow. The connector will reject incoming packets if they would put it above this balance. The format is a string containing an integer (which may be prefixed with `-` to indicate a negative value), `"-Infinity"` or `"Infinity"`.
         */
        maximum: string;
        /**
         * Balance (in this account's indivisible base units) numerically below which the connector will automatically initiate a settlement. The format is a string containing an integer (which may be prefixed with `-` to indicate a negative value) or `"-Infinity"`.
         */
        settleThreshold?: string;
        /**
         * Balance (in this account's indivisible base units) the connector will attempt to reach when settling. The format is an integer (which may be prefixed with `-` to indicate a negative value) as a string.
         */
        settleTo?: string;
      };
      /**
       * Maximum amount per packet for incoming prepare packets. Connector will reject any incoming prepare packets from this account with a higher amount. Amount should be provided as an integer in a string (in atomic units). This setting is enforced by the built-in `maxPacketAmount` middleware.
       */
      maxPacketAmount?: string;
      /**
       * Configuration to limit the total amount sent via Interledger per unit of time. This setting is enforced by the built-in `throughput` middleware.
       */
      throughput?: {
        /**
         * Length of time (in milliseconds) during which the token balance increases by `incomingAmount`/`outgoingAmount` tokens. Defaults to one second.
         */
        refillPeriod?: number;
        /**
         * Maximum incoming throughput amount (in atomic units; per second) for incoming packets. If this setting is not set, the incoming throughput limit is disabled.
         */
        incomingAmount?: string;
        /**
         * Maximum throughput amount (in atomic units; per second) for outgoing packets. If this setting is not set, the outgoing throughput limit is disabled.
         */
        outgoingAmount?: string;
      };
      /**
       * Maximum rate of incoming packets. Limit is implemented as a token bucket with a constant refill rate. When the token bucket is empty, all requests are immediately rejected. This setting is enforced by the built-in `rateLimit` middleware.
       */
      rateLimit?: {
        /**
         * Length of time (in milliseconds) during which the token balance increases by `refillCount` tokens. Defaults to one second.
         */
        refillPeriod?: number;
        /**
         * How many tokens are refilled per period. The default refill period is one second, so this would be the average number of requests per second.
         */
        refillCount?: number;
        /**
         * Maximum number of tokens in the bucket.
         */
        capacity?: number;
      };
      /**
       * Prevents sending duplicate packets.
       */
      deduplicate?: {
        /**
         * Frequency at which the connector removes old deduplicate records. (in milliseconds, default: 30000)
         */
        cleanupInterval?: number;
        /**
         * Lifetime of a cache record. (in milliseconds, default: 30000)
         */
        packetLifetime?: number;
      };
      /**
       * Whether we should broadcast routes to this peer. Defaults to `false` for `relation=child` and `true` otherwise.
       */
      sendRoutes?: boolean;
      /**
       * Whether we should receive and process route broadcasts from this peer. Defaults to `false` for `relation=child` and `true` otherwise.
       */
      receiveRoutes?: boolean;
      /**
       * Options that will be passed to the plugin constructor.
       */
      options?: {
        [k: string]: any;
      };
      /**
       * What segment will be appended to the connector's ILP address to form this account's ILP address. Only applicable to accounts with `relation=child`. Defaults to the id of the account, i.e. the key used in the `accounts` config object.
       */
      ilpAddressSegment?: string;
    };
  };
  /**
   * Which account should be used as the default route for all other traffic. Can be set to empty string to disable the default route or 'auto' to automatically use the first parent in the `accounts` map. Default: 'auto'
   */
  defaultRoute?: string;
  /**
   * Additional routes to add to the connector's routing table.
   */
  routes?: {
    /**
     * ILP address prefix that this route applies to. Configured routes take precedence over the same or shorter prefixes that are local or published by peers. More specific prefixes will still take precedence. Prefixes should NOT include a trailing period.
     */
    targetPrefix: string;
    /**
     * ID of the account that destinations matching `targetPrefix` should be forwarded to. Must be one of the accounts in `accounts`.
     */
    peerId: string;
  }[];
  /**
   * How much of a spread to add on top of the reference exchange rate. Determines the connector's margin.
   */
  spread?: number;
  /**
   * Minimum time the connector wants to budget for getting a message to the accounts its trading on. In milliseconds.
   */
  minMessageWindow?: number;
  /**
   * Maximum duration (in milliseconds) the connector is willing to place funds on hold while waiting for the outcome of a transaction.
   */
  maxHoldTime?: number;
  /**
   * Whether to broadcast known routes.
   */
  routeBroadcastEnabled?: boolean;
  /**
   * Frequency at which the connector broadcasts its routes to adjacent connectors. (in milliseconds)
   */
  routeBroadcastInterval?: number;
  /**
   * The frequency at which the connector checks for expired routes. (in milliseconds)
   */
  routeCleanupInterval?: number;
  /**
   * The maximum age of a route provided by this connector. (in milliseconds)
   */
  routeExpiry?: number;
  /**
   * Seed used for generating routing table auth values.
   */
  routingSecret?: string;
  /**
   * Name of the backend (can be built-in or a require-able module name). Built-in modules are: ecb, ecb-plus-xrp, ecb-plus-coinmarketcap, one-to-one
   */
  backend?: string;
  /**
   * Additional configuration for the backend.
   */
  backendConfig?: {
    [k: string]: any;
  };
  /**
   * Name of the store (can be built-in or a require-able module name). Built-in modules are: memory
   */
  store?: string;
  /**
   * Shorthand for `config.storeConfig.path`.
   */
  storePath?: string;
  /**
   * Additional options to be passed to the `store`'s constructor.
   */
  storeConfig?: {
    [k: string]: any;
  };
  /**
   * Additional middleware that should be added to the middleware stack.
   */
  middlewares?: {
    /**
     * Object describing middleware instance.
     */
    [k: string]: {
      /**
       * NPM module that should be `require`d to load the middleware constructor.
       */
      type: string;
      /**
       * Options that will be passed to the middleware constructor.
       */
      options?: {
        [k: string]: any;
      };
      [k: string]: any;
    };
  };
  /**
   * List of built-in middlewares that should be disabled. Can be used to replace built-in middleware with your own custom version.
   */
  disableMiddleware?: (
    | "errorHandler"
    | "rateLimit"
    | "balance"
    | "maxPacketAmount"
    | "throughput"
    | "deduplicate"
    | "validateFulfillment"
    | "echo"
    | "expire"
    | "stats"
    | "alert")[];
  /**
   * Whether to allow routing payments back to the account that sent them.
   */
  reflectPayments?: boolean;
  /**
   * How long the connector should wait for account plugins to connect before launching other subsystems. (in milliseconds)
   */
  initialConnectTimeout?: number;
  /**
   * Whether the admin API is enabled or not. Default: false (disabled)
   */
  adminApi?: boolean;
  /**
   * Which port the admin API should listen on. Default: 7780
   */
  adminApiPort?: number;
  /**
   * Host to bind to. Warning: The admin API interface should never be made public! Default: '127.0.0.1'
   */
  adminApiHost?: string;
  /**
   * Whether the Prometheus exporter should include system metrics or not. Default: false (no)
   */
  collectDefaultMetrics?: boolean;
}
